import React, { useState } from "react";
import { Button } from "@component/button";
import { FileTextIcon, XIcon, UploadIcon } from "lucide-react";
import { MAX_FILE_SIZE } from "@constant/upload";
import { Input } from "@component/input";
import { uploadCV } from "@service/cv";
import { parseFormat } from "@util/date";

interface Props {
  path: string;
  uploaded_at: string;
  onCallback: () => void;
}

export function ResumeDocument(props: Props) {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");

  const validateFile = (file: File): string | null => {
    if (file.size > MAX_FILE_SIZE) {
      return "File size must be less than 5MB.";
    }
    return null;
  };

  interface FileUploadResponse {
    success: boolean;
    message?: string;
  }

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    const error = validateFile(file);
    if (error) {
      setUploadError(error);
      return;
    }

    setIsUploading(true);
    setUploadError("");

    try {
      const response: FileUploadResponse = await uploadCV(file);
      if (response.success) {
        props.onCallback();
      } else {
        setUploadError(response.message || "Failed to upload CV.");
      }
    } catch (error: unknown) {
      console.error("CV upload error:", error);
      setUploadError("An unexpected error occurred during upload.");
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <>

      {props.path === "" ? (
        <div className="flex flex-col items-center justify-center px-4 py-6">
          <p className="text-sm text-gray-500 text-center mb-4">
            Upload your resume to automatically fill your profile information.
          </p>
          <Button
            disabled={isUploading}
            className="flex items-center gap-2 relative cursor-pointer"
          >

            {isUploading ? (
              <>
                <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Processing
              </>
            ) : (
              <>
                <UploadIcon className="h-4 w-4" />
                Upload Resume
              </>
            )}
            <Input
              type="file"
              accept=".pdf,.docx"
              className="absolute inset-0 opacity-0 cursor-pointer"
              onChange={handleFileUpload}
            />
          </Button>
        </div>
      ) : (
        <div className="border border-gray-200 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-12 hidden sm:flex bg-gray-100 rounded items-center justify-center">
              <FileTextIcon className="h-6 w-6 text-gray-400" />
            </div>

            <div className="flex-1">
              <h3 className="font-medium text-gray-900">{props.path}</h3>
              <p className="text-sm text-gray-600">
                Uploaded on {parseFormat(props.uploaded_at, "D MMMM YYYY")}
              </p>
            </div>

            <div className="flex gap-2">
              {uploadError && (
                <div className="text-red-500 text-sm flex items-center">
                  <XIcon className="h-4 w-4 mr-1" /> {uploadError}
                </div>
              )}
              <Button
                disabled={isUploading}
                variant="outline"
                size="sm"
                className="flex items-center gap-1 bg-white border-gray-300 text-gray-700 hover:bg-gray-100 relative cursor-pointer"
              >

                {isUploading ? (
                  <>
                    <svg className="animate-spin h-4 w-4 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing
                  </>
                ) : (
                  <>
                    <UploadIcon className="h-4 w-4 cursor-pointer" />
                    Update
                  </>
                )}
                <Input
                  type="file"
                  accept=".pdf,.docx"
                  className="absolute inset-0 opacity-0 cursor-pointer"
                  onChange={handleFileUpload}
                />
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
